import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import LeadDetailsSidebar from '@/components/LeadDetails/LeadDetailsSidebar'
import { Lead } from '@/types/leads/Table'
import { useUsers } from '@/contexts/UsersContext'
import { getLeadDetails } from '@/actions/lead-manager'

interface UserOption {
  value: string;
  label: string;
  email?: string;
  role?: string;
}

const LeadDetail: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const { activeUsers } = useUsers()
  const [lead, setLead] = useState<Lead | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Transform active users from context to dropdown format
  const activeUsersForDropdown: UserOption[] = useMemo(() => {
    return activeUsers.map((u) => ({
      value: u.uuid,
      label: u.userName,
      email: u.userEmail,
      role: u.role?.role_name
    }));
  }, [activeUsers]);

  // Fetch lead details on mount
  useEffect(() => {
    const fetchLead = async () => {
      if (!uuid) {
        setError('No lead ID provided')
        setIsLoading(false)
        return
      }

      try {
        const response = await getLeadDetails(uuid)
        if (response.success && response.data) {
          setLead(response.data as Lead)
        } else {
          setError('Lead not found')
        }
      } catch (err) {
        console.error('Error fetching lead:', err)
        setError('Failed to load lead')
      } finally {
        setIsLoading(false)
      }
    }

    fetchLead()
  }, [uuid])

  const handleClose = () => {
    navigate('/leads')
  }

  const handleLeadUpdate = async () => {
    // Refetch lead details after update
    if (uuid) {
      const response = await getLeadDetails(uuid)
      if (response.success && response.data) {
        setLead(response.data as Lead)
      }
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brandColor mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading lead details...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !lead) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error || 'Lead not found'}</p>
          <button
            onClick={() => navigate('/leads')}
            className="mt-4 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-opacity-90"
          >
            Back to Leads
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <LeadDetailsSidebar
        isOpen={true}
        onClose={handleClose}
        lead={lead}
        activeUsersForDropdown={activeUsersForDropdown}
        onUpdate={handleLeadUpdate}
        mode="page"
      />
    </>
  )
}

export default LeadDetail
